import { Box, Divider, styled, Typography, RadioGroup } from '@mui/material';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { NotificationSetting, NotificationType } from '../../../../../models/CompanyOnboardingModels';
import UserDetailsContext from '../../../../../contexts/UserDetailsContext/UserDetailsContext';

import { RootState } from '../../../../../stores/Store';
import { updateRequest } from '../../../../../stores/AssignDeviceStore';

import Button from '../../../../../components/Button/Button';
import ButtonGroup from '../../../../../components/ButtonGroup/ButtonGroup';
import Radio from '../../../../../components/Radio/Radio';
import Checkbox from '../../../../../components/Checkbox/Checkbox';
import SectionTitle from '../../../../../components/Text/TextStyles';

import useAssignDevice from './useAssignDevice';

const RowsContainer = styled(Box)(() => ({
  paddingBottom: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
}));

const Row = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
}));

const SectionValue = styled(Typography)(() => ({
  lineHeight: 'normal',
  marginLeft: 'auto',
  fontWeight: '700',
}));

const SectionLabel = styled(Typography)(() => ({
  lineHeight: 'normal',
}));

interface TerminateConnectionSummaryStepProps {
  onClose: () => void;
  onAssignSuccess: () => void;
}

function TerminateConnectionSummaryStep({ onClose, onAssignSuccess }: TerminateConnectionSummaryStepProps) {
  const [t] = useTranslation();
  const { request, hasDistributionList, deviceToAssign, qrCodeNotificationSettings } = useSelector(
    (state: RootState) => state.assignDeviceReducer,
  );
  const dispatch = useDispatch();
  const { assignDevice, isLoading } = useAssignDevice();
  const user = useContext(UserDetailsContext);

  /**
   * Executes the assign user request
   */
  const handleConfirmButton = () => {
    assignDevice(() => {
      onClose();
      onAssignSuccess();
    });
  };

  /**
   * Handles change of the radio button
   * @param e
   */
  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value as NotificationType;
    dispatch(updateRequest({ notificationRecipient: val }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', paddingTop: '1rem' }}>
      {/* Summary */}
      <SectionTitle>{t('pages.devicesReport.assignUserDrawer.summary')}</SectionTitle>
      <RowsContainer>
        <Row>
          <SectionLabel>{t('pages.devicesReport.assignUserDrawer.userName')}</SectionLabel>
          <SectionValue>{`${user?.firstName} ${user?.lastName}`}</SectionValue>
        </Row>
        <Row>
          <SectionLabel>{t('pages.devicesReport.assignUserDrawer.device')}</SectionLabel>
          <SectionValue>{`${deviceToAssign?.manufacturer} ${deviceToAssign?.model}`}</SectionValue>
        </Row>
        <Row>
          <SectionLabel sx={{ width: '25%' }}>{t('pages.devicesReport.assignUserDrawer.eid')}</SectionLabel>
          <SectionValue sx={{ width: '75%', textAlign: 'end' }}>{deviceToAssign?.eid}</SectionValue>
        </Row>
      </RowsContainer>

      {/* Notification */}
      <SectionTitle sx={{ paddingTop: '1rem' }}>
        {t('pages.devicesReport.assignUserDrawer.notificationSettings')}
      </SectionTitle>
      <Box sx={{ paddingBottom: '1rem' }}>
        <Typography sx={{ paddingBottom: '1.5rem' }}>
          {t('pages.devicesReport.assignUserDrawer.notificationQuestion')}
        </Typography>
        <RadioGroup
          aria-labelledby="qr-code-destination"
          name="qr-code-destination"
          value={request.notificationRecipient}
          onChange={handleRadioChange}
        >
          {qrCodeNotificationSettings.map((ns: NotificationSetting) => (
            <Radio
              value={ns.type}
              key={ns.type}
              label={
                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                  {t(`pages.devicesReport.assignUserDrawer.${ns.type}`)}
                  <Typography sx={{ paddingLeft: '1rem', color: (theme) => theme.palette.text.disabled }}>
                    {ns.label}
                  </Typography>
                </Box>
              }
              disabled={isLoading}
            />
          ))}
        </RadioGroup>
      </Box>

      <Divider
        sx={{
          borderColor: (theme) => theme.palette.monochrome.monoChrome3,
          marginBottom: '1.5rem',
        }}
      />
      <Box sx={{ marginBottom: '1rem' }}>
        <Checkbox
          checked={request.notifyAdmin}
          onChange={(e) => {
            dispatch(updateRequest({ notifyAdmin: e.currentTarget.checked }));
          }}
          label={t('pages.devicesReport.assignUserDrawer.sendMeNotifications')}
        />
      </Box>
      {hasDistributionList ? (
        <Box sx={{ marginBottom: '2.5rem' }}>
          <Checkbox
            checked={request.notifyDistributionList}
            onChange={(e) => {
              dispatch(updateRequest({ notifyDistributionList: e.currentTarget.checked }));
            }}
            label={t('pages.devicesReport.assignUserDrawer.sendDLNotifications')}
          />
        </Box>
      ) : null}

      <Box sx={{ marginTop: 'auto' }}>
        <ButtonGroup>
          <Button label={t('common.cancel')} color="alternative2Button" onClick={onClose} />
          <Button
            label={t('common.confirm')}
            color="primaryButton"
            disabled={isLoading || sessionStorage.getItem('impersonateId') !== null}
            onClick={handleConfirmButton}
          />
        </ButtonGroup>
      </Box>
    </Box>
  );
}

export default TerminateConnectionSummaryStep;
